import React, { useEffect } from "react";
import { IntlProvider, injectIntl } from "@edx/frontend-platform/i18n";
import { messages as footerMessages } from "@edx/frontend-component-footer";
import { messages as headerMessages } from "@edx/frontend-component-header";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import useIsSpeechCraftMFE from "./hooks/useIsSpeechcraftMFE";
import { getAuthenticatedUser } from "@edx/frontend-platform/auth";

import {
  fetchThemeData,
  fetchNotificationsData,
  fetchEventData,
  fetchRolesData,
  fetchLocaleData,
} from "./data/thunks";
import PageLoading from "./generic/PageLoading";
import messages from "./tab-page/messages";
import appMessages from "./i18n";
import useUserLoginRedirect from "./hooks/useUserLoginRedirect";
import FullPageSkeleton from "./components/Skeletons/FullPageSkeleton";

const addStylesheetURL = (url) => {
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = url;
  link.type = "text/css";
  document.head.appendChild(link);

  return link;
};

const addCssRules = (theme = {}) => {
  let cssRules = "";

  cssRules += `--primary: ${theme.primary_color}; `;
  cssRules += `--primary-light: ${theme.primary_active_color}; `;
  cssRules += `--primary-dark: ${theme.primary_hover_color} ;`;
  cssRules += `--secondary: ${theme.secondary_color} ;`;
  cssRules += `--secondary-light: ${theme.secondary_active_color} ;`;
  cssRules += `--secondary-dark: ${theme.secondary_hover_color} ;`;
  cssRules += `--grey-tint1: ${theme.grey_1} ;`;
  cssRules += `--grey-tint2: ${theme.grey_2} ;`;
  cssRules += `--grey-shade1: ${theme.grey_3} ;`;
  cssRules += `--grey-shade2: ${theme.grey_4};`;
  cssRules += `--fontFamilyLato: ${theme.body_font_family} ;`;
  cssRules += `--success: ${theme.semantic_1} ;`;
  cssRules += `--error: ${theme.semantic_2} ;`;
  cssRules += `--tertiary1: ${theme.tertiary_1} ;`;
  cssRules += `--tertiary2: ${theme.tertiary_2} ;`;
  cssRules += `--tertiary3: ${theme.tertiary_3} ;`;
  cssRules += `--tertiary4: ${theme.tertiary_4} ;`;
  cssRules += `--tertiary5: ${theme.tertiary_5} ;`;
  cssRules += `--tertiary6: ${theme.tertiary_6} ;`;
  cssRules += `--tertiary7: ${theme.tertiary_7} ;`;
  cssRules += `--tertiary8: ${theme.tertiary_8} ;`;
  cssRules += `--tertiary9: ${theme.tertiary_9} ;`;
  cssRules += `--tertiary10: ${theme.tertiary_10} ;`;
  cssRules += `--tertiary11: ${theme.tertiary_11} ;`;
  cssRules += `--tertiary12: ${theme.tertiary_12} ;`;
  cssRules += `--focus-color: ${theme.tertiary_3} ;`;
  cssRules += `--body-text: ${theme.grey_4} ;`;

  const head = document.head || document.getElementsByTagName("head")[0];
  const style = document.createElement("style");
  head.appendChild(style);
  style.type = "text/css";

  if (style.styleSheet) {
    // This is required for IE8 and below.
    style.styleSheet.cssText = `:root { ${cssRules} };`;
  } else {
    style.appendChild(document.createTextNode(`:root { ${cssRules} };`));
  }

  return style;
};

const RTL_SUPPORTED_LANGUAGES = [
  "ar",
  "arc",
  "dv",
  "fa",
  "ha",
  "he",
  "khw",
  "ks",
  "ku",
  "ps",
  "ur",
  "yi",
];

const isRTL = (lang = string) =>
  RTL_SUPPORTED_LANGUAGES.find((elem) => elem === lang);

function LocalizationProvider({ intl, children }) {
  useUserLoginRedirect();
  const dispatch = useDispatch();
  const user = getAuthenticatedUser();
  const { isAppInitializing, theme, locale, isFetchingLocale } = useSelector(
    (state) => state.app
  );

  const isSpeechCraftMFE = useIsSpeechCraftMFE();
  const location = useLocation();
  const courseIdFromUrl = location.pathname.split("/")[2];

  useEffect(() => {
    if (!courseIdFromUrl || !user) {
      return;
    }

    dispatch(fetchThemeData());
    dispatch(fetchLocaleData(courseIdFromUrl));
    dispatch(fetchNotificationsData());
    if (isSpeechCraftMFE) {
      dispatch(fetchEventData());
    }
    dispatch(fetchRolesData());
  }, [courseIdFromUrl, user]);

  useEffect(() => {
    if (!theme.length) {
      return;
    }

    const cssRulesStyle = addCssRules(theme[0]);
    const link = addStylesheetURL(
      `https://fonts.googleapis.com/css2?family=${theme[0]["body_font_family"]}:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap`
    );

    return () => {
      document.head.removeChild(link);
      document.head.removeChild(cssRulesStyle);
    };
  }, [JSON.stringify(theme)]);

  useEffect(() => {
    let html = document.getElementsByTagName("html")[0];

    html.setAttribute("dir", isRTL(locale) ? "rtl" : "ltr");
  }, [locale]);

  return (
    <>
      {isAppInitializing || isFetchingLocale ? (
        <FullPageSkeleton isHeader={true} srMessage={intl.formatMessage(messages.loading)} />
      ) : (
        <IntlProvider
          locale={locale}
          defaultLocale="en"
          messages={{
            ...appMessages[locale],
            // ...footerMessages[locale],
            // ...headerMessages[locale],
          }}
        >
          {children}
        </IntlProvider>
      )}
    </>
  );
}

export default injectIntl(LocalizationProvider);
